import { EmployerSignupStatus } from '@zorro/clients';
import {
  useAppRouter,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { useCompanyDetailsForm } from '../../CompanyDetailsForm';
import { useCompanySetupForm } from '../../CompanySetupForm';
import { useContactsForm } from '../../ContactsForm';
import {
  AddEmployerStep,
  AddEmployerStepType,
  HandleStepSubmit,
} from '../AddEmployerStep';
import { CompanyDetailsCard } from './Cards/CompanyDetailsCard';
import { CompanySetupCard } from './Cards/CompanySetupCard';
import { ContactsCard } from './Cards/ContactsCard';

const isDon = true;
const isFinalizationMode = true;
const isSignedAgreementVisible = true;

type Props = {
  employerId: string;
};

export const EmployerReviewForm = ({ employerId }: Props) => {
  'use no memo';
  const { navigateToRoute } = useAppRouter();
  const { mutate: mutateEmployer } = useMonolithMutation({
    method: 'employersControllerUpdate',
    shouldReloadPage: false,
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { companyDetailsForm, isLynxPaymentsEnabled } = useCompanyDetailsForm({
    employerId,
    isFinalizationMode,
    isSignedAgreementVisible,
    isDon,
  });
  const { contactsForm } = useContactsForm(employerId, isFinalizationMode);
  const { companySetupForm } = useCompanySetupForm(
    employerId,
    isFinalizationMode
  );

  const isSubmitDisabled =
    !companyDetailsForm.formState.isValid ||
    !contactsForm.formState.isValid ||
    !companySetupForm.formState.isValid;

  const handleSubmit: HandleStepSubmit = async () => {
    if (!employer) return { navigate: false };

    try {
      await mutateEmployer([
        employerId,
        {
          zorroPartnerId: employer.zorroPartnerId,
          signupStatus: EmployerSignupStatus.NEEDS_REVIEW,
        },
      ]);
      navigateToRoute('DON_ADD_EMPLOYER_THANK_YOU', [employerId]);
    } catch {
      /* empty */
    }

    return { navigate: false };
  };

  return (
    <AddEmployerStep
      submitLabel="Complete company profile"
      isFinalizationMode={isFinalizationMode}
      isSubmitDisabled={isSubmitDisabled}
      step={AddEmployerStepType.REVIEW}
      title="Review employer info"
      onSubmit={handleSubmit}
      employerId={employerId}
      isDon={isDon}
    >
      <CompanyDetailsCard
        isSignedAgreementRequired={false}
        shouldShowSignedAgreement={false}
        isFinalizationMode={isFinalizationMode}
        isValid={companyDetailsForm.formState.isValid}
        fields={companyDetailsForm.watch()}
        employerId={employerId}
        isDon={isDon}
        isLynxPaymentsEnabled={isLynxPaymentsEnabled}
      />
      <ContactsCard
        isFinalizationMode={isFinalizationMode}
        isValid={contactsForm.formState.isValid}
        fields={contactsForm.watch()}
        employerId={employerId}
        isDon={isDon}
      />
      <CompanySetupCard
        isFinalizationMode={isFinalizationMode}
        isValid={companySetupForm.formState.isValid}
        fields={companySetupForm.watch()}
        employerId={employerId}
        isDon={isDon}
      />
    </AddEmployerStep>
  );
};
